import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Check, Copy, Loader2, Pencil, Trash2, X } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { useSearchParams } from 'react-router-dom';
import SemanticThemePreviewCard from '@/components/theme/SemanticThemePreviewCard';
import EventSettings from '@/panel/components/EventSettings';
import EventPulsingBadge from '@/panel/components/EventPulsingBadge';
import { useEventDetails } from './EventDetailsContext';
import DesignTab from './DesignTab';
import EventDetailsDialogs from './EventDetailsDialogs';
import GeneralTab from './GeneralTab';
import ShareTab from './ShareTab';
import type { ThemeTokens } from '@/types/theme';

type EventDetailsTab = 'general' | 'design' | 'share' | 'settings';
const EVENT_DETAILS_TABS: EventDetailsTab[] = ['general', 'design', 'share', 'settings'];

function getInitialTab(value: string | null): EventDetailsTab {
  return EVENT_DETAILS_TABS.includes(value as EventDetailsTab) ? (value as EventDetailsTab) : 'general';
}

type InlineEditableFieldProps = {
  savedValue: string;
  draftValue: string;
  onDraftChange: (nextValue: string) => void;
  onSave: (nextValue: string) => Promise<boolean>;
  placeholder: string;
  maxLength?: number;
  verticalAlign?: 'start' | 'center';
  required?: boolean;
  dashedWhenEmpty?: boolean;
  maxWidthClassName?: string;
  wrapperClassName: string;
  inputClassName: string;
  iconAlignClassName?: string;
};

const InlineEditableField: React.FC<InlineEditableFieldProps> = ({
  savedValue,
  draftValue,
  onDraftChange,
  onSave,
  placeholder,
  maxLength,
  verticalAlign = 'start',
  required = false,
  dashedWhenEmpty = false,
  maxWidthClassName = '',
  wrapperClassName,
  inputClassName,
  iconAlignClassName = 'mt-1',
}) => {
  const [saving, setSaving] = useState(false);
  const [inputWidth, setInputWidth] = useState<number | undefined>(undefined);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const measureRef = useRef<HTMLSpanElement | null>(null);

  const isDirty = draftValue !== savedValue;
  const isInvalid = required && draftValue.trim().length === 0;
  const isEmpty = draftValue.trim().length === 0;
  const isDashedEmptyState = dashedWhenEmpty && isEmpty;
  const usesCenteredAlignment = isDashedEmptyState || verticalAlign === 'center';
  const effectiveIconAlignClassName = usesCenteredAlignment ? '' : iconAlignClassName;

  useLayoutEffect(() => {
    if (!measureRef.current) {
      return;
    }

    setInputWidth(measureRef.current.offsetWidth + 2);
  }, [draftValue, placeholder, inputClassName]);

  const handleSave = async () => {
    if (saving || !isDirty || isInvalid) {
      return;
    }

    const nextValue = (() => {
      const baseValue = required ? draftValue.trim() : draftValue;
      return typeof maxLength === 'number' ? baseValue.slice(0, maxLength) : baseValue;
    })();

    setSaving(true);
    const saved = await onSave(nextValue);
    setSaving(false);

    if (saved) {
      onDraftChange(nextValue);
    }
  };

  const handleCancel = () => {
    if (saving) {
      return;
    }

    onDraftChange(savedValue);
    inputRef.current?.blur();
  };

  return (
    <div className={['block', wrapperClassName].join(' ')}>
      <div
        className={[
          'group relative inline-flex max-w-full gap-2 rounded-2xl transition-all',
          usesCenteredAlignment ? 'items-center' : 'items-start',
          maxWidthClassName,
          isDashedEmptyState ? 'border border-dashed border-slate-300 px-3 py-2' : '',
          isInvalid && isDirty ? 'bg-rose-50/60 ring-1 ring-rose-200' : '',
        ].join(' ')}
      >
        <span
          ref={measureRef}
          aria-hidden="true"
          className={[
            'pointer-events-none absolute left-0 top-0 invisible whitespace-pre',
            inputClassName,
          ].join(' ')}
        >
          {draftValue || placeholder}
        </span>

        <input
          ref={inputRef}
          type="text"
          value={draftValue}
          maxLength={maxLength}
          style={inputWidth ? { width: `${inputWidth}px` } : undefined}
          onChange={(event) => {
            const nextValue = typeof maxLength === 'number'
              ? event.target.value.slice(0, maxLength)
              : event.target.value;
            onDraftChange(nextValue);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              void handleSave();
              return;
            }

            if (event.key === 'Escape') {
              event.preventDefault();
              handleCancel();
            }
          }}
          placeholder={placeholder}
          aria-invalid={isInvalid}
          className={[
            'min-w-0 max-w-full bg-transparent outline-none placeholder:text-slate-400',
            inputClassName,
          ].join(' ')}
        />

        <div
          className={[
            'shrink-0 flex items-center gap-1.5',
            effectiveIconAlignClassName,
            isDirty ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100',
          ].join(' ')}
        >
          {isDirty ? (
            <>
              <button
                type="button"
                onClick={() => {
                  void handleSave();
                }}
                disabled={saving || isInvalid}
                aria-label="Guardar cambio"
                className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-violet-600 text-white shadow-sm transition hover:bg-violet-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
              </button>

              <button
                type="button"
                onClick={handleCancel}
                disabled={saving}
                aria-label="Cancelar cambio"
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white/90 text-slate-500 transition hover:border-slate-300 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent disabled:cursor-not-allowed disabled:opacity-50"
              >
                <X className="h-4 w-4" />
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => {
                inputRef.current?.focus();
              }}
              aria-label="Editar valor"
              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition hover:text-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
            >
              <Pencil className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const EventDetailsShell: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<EventDetailsTab>(() => getInitialTab(searchParams.get('tab')));
  const [previewTokens, setPreviewTokens] = useState<ThemeTokens | null>(null);
  const {
    event,
    title,
    settingsValues,
    updateSetting,
    albumUrl,
    badgeState,
    copyEventId,
    setDeleteDialogOpen,
    coverImageUrl,
    fileInputRef,
    newCoverPreviewUrl,
    savingCover,
    openCoverFileDialog,
    onCoverFileChange,
    onApplyNewCover,
    resetNewCover,
    normalizedTheme,
  } = useEventDetails();

  const editableTitleValue = settingsValues.text_home_h1?.trim() ? settingsValues.text_home_h1 : title || '';
  const editableDescriptionValue = settingsValues.text_home_description || '';
  const [titleDraft, setTitleDraft] = useState(editableTitleValue);
  const [descriptionDraft, setDescriptionDraft] = useState(editableDescriptionValue);

  useEffect(() => {
    setPreviewTokens(normalizedTheme.tokens);
  }, [normalizedTheme.tokens]);

  useEffect(() => {
    setTitleDraft(editableTitleValue);
  }, [editableTitleValue]);

  useEffect(() => {
    setDescriptionDraft(editableDescriptionValue);
  }, [editableDescriptionValue]);

  if (!event) return null;

  const handleTabChange = (value: string) => {
    const nextTab = getInitialTab(value);
    setActiveTab(nextTab);

    const nextParams = new URLSearchParams(searchParams);
    if (nextTab === 'general') {
      nextParams.delete('tab');
    } else {
      nextParams.set('tab', nextTab);
    }
    setSearchParams(nextParams, { replace: true });
  };

  return (
    <div className="space-y-6">
      <div className="px-1 py-2">
        <div className="flex min-h-[72px] items-start justify-between gap-6">
          <div className="min-w-0 flex-1 space-y-2">
            <InlineEditableField
              savedValue={editableTitleValue}
              draftValue={titleDraft}
              onDraftChange={setTitleDraft}
              onSave={(nextValue) => updateSetting('text_home_h1', nextValue)}
              placeholder="Título del evento"
              maxLength={35}
              required
              maxWidthClassName="max-w-3xl"
              wrapperClassName="-ml-3 px-3 py-1"
              inputClassName="text-2xl font-semibold text-slate-800"
              iconAlignClassName="mt-1.5"
            />

            <InlineEditableField
              savedValue={editableDescriptionValue}
              draftValue={descriptionDraft}
              onDraftChange={setDescriptionDraft}
              onSave={(nextValue) => updateSetting('text_home_description', nextValue)}
              placeholder="Agregar subtítulo opcional"
              maxLength={60}
              verticalAlign="center"
              dashedWhenEmpty
              maxWidthClassName="max-w-3xl"
              wrapperClassName="-ml-3 px-3 py-1"
              inputClassName="text-base text-slate-500"
            />
          </div>

          <div className="flex shrink-0 flex-col items-end gap-3">
            {badgeState.show && (
              <EventPulsingBadge variant={badgeState.variant} label={badgeState.label} />
            )}
          </div>
        </div>
      </div>

      <div className="relative grid gap-6 xl:grid-cols-[minmax(0,1fr)_340px]">
        <Button
          asChild={!!albumUrl}
          disabled={!albumUrl}
          className="absolute right-0 -top-32  lg:top-0 z-10 h-12 w-[120px] rounded-xl bg-purple-600 px-6 text-base font-semibold uppercase text-white shadow-[0_18px_30px_rgba(124,77,255,0.25)] hover:bg-purple-500 hover:text-white sm:w-[160px] md:w-[200px] xl:hidden"
        >
          {albumUrl ? (
            <a href={albumUrl} target="_blank" rel="noopener noreferrer">
              Acceder
            </a>
          ) : (
            <span>Acceder</span>
          )}
        </Button>

        <Tabs value={activeTab} onValueChange={handleTabChange} className="min-w-0 space-y-6">
          <div className="overflow-x-auto">
            <TabsList className="min-w-max rounded-2xl border border-slate-200 bg-white/80 p-1 shadow-sm">
              <TabsTrigger value="general" className="rounded-xl px-5 py-2.5 data-[state=active]:text-violet-600">General</TabsTrigger>
              <TabsTrigger value="design" className="rounded-xl px-5 py-2.5 data-[state=active]:text-violet-600">Diseño</TabsTrigger>
              <TabsTrigger value="share" className="rounded-xl px-5 py-2.5 data-[state=active]:text-violet-600">Compartir</TabsTrigger>
              <TabsTrigger value="settings" className="rounded-xl px-5 py-2.5 data-[state=active]:text-violet-600">Configuración</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="general" forceMount className="mt-0 data-[state=inactive]:hidden">
            <GeneralTab />
          </TabsContent>

          <TabsContent value="design" forceMount className="mt-0 data-[state=inactive]:hidden">
            <DesignTab onPreviewTokensChange={setPreviewTokens} />
          </TabsContent>

          <TabsContent value="share" forceMount className="mt-0 data-[state=inactive]:hidden">
            <ShareTab />
          </TabsContent>

          <TabsContent value="settings" forceMount className="mt-0 data-[state=inactive]:hidden">
            <EventSettings />
          </TabsContent>
        </Tabs>

        <div className="space-y-6 xl:pt-1">
          <Button
            asChild={!!albumUrl}
            disabled={!albumUrl}
            className="hidden h-12 w-full rounded-xl bg-purple-600 px-6 text-base font-semibold uppercase text-white shadow-[0_18px_30px_rgba(124,77,255,0.25)] hover:bg-purple-500 hover:text-white xl:inline-flex"
          >
            {albumUrl ? (
              <a href={albumUrl} target="_blank" rel="noopener noreferrer">
                Acceder
              </a>
            ) : (
              <span>Acceder</span>
            )}
          </Button>

          <SemanticThemePreviewCard
            tokens={previewTokens ?? normalizedTheme.tokens}
            title={titleDraft}
            subtitle={descriptionDraft}
            language={settingsValues.lang}
            coverImageUrl={coverImageUrl}
            newCoverPreviewUrl={newCoverPreviewUrl}
            showGuestbook={settingsValues.enable_guestbook === 'true'}
            showInvitation={Boolean(settingsValues.bloomdate_url?.trim())}
            savingCover={savingCover}
            fileInputRef={fileInputRef}
            onCoverFileChange={onCoverFileChange}
            openCoverFileDialog={openCoverFileDialog}
            onApplyNewCover={onApplyNewCover}
            resetNewCover={resetNewCover}
            showSupportCard={activeTab === 'design'}
          />
        </div>
      </div>

      <div className="text-xs text-slate-400 flex items-center justify-between flex-wrap gap-2">
        <span className="uppercase flex items-center gap-2">
          <strong>EVENT ID:</strong>
          {event.id}
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="h-7 w-7 text-slate-400 hover:text-slate-600"
            onClick={() => { void copyEventId(); }}
            aria-label="Copiar identificador interno"
          >
            <Copy className="h-3.5 w-3.5" />
          </Button>
        </span>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setDeleteDialogOpen(true)}
            className="flex items-center gap-1 text-xs font-semibold text-rose-500 hover:text-rose-600 transition"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Borrar evento
          </button>
        </div>
      </div>

      <EventDetailsDialogs />
    </div>
  );
};

export default EventDetailsShell;
